import { Component, Host, h, ComponentInterface, Prop, State, Element, EventEmitter, Event, Listen } from '@stencil/core';

@Component({
  tag: 'vds-add-to-press-binder',
  styleUrl: 'vds-add-to-press-binder.scss',
  shadow: true,
})
export class VdsAddToPressBinder implements ComponentInterface {

  @Element() el!: HTMLVdsAddToPressBinderElement;

  /**
   * The nodes' GUID that is used to create the press-binder store
   */
  @Prop() nodeguid: string;

  /**
   * Path to Lottie file
   */
  @Prop() animation: string;

  /**
   * Displayed text
   */
  @Prop() label: string;

  /**
   * Is true when store contains nodeguid
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Local store of nodeguids in session-storage
   */
  @State() store: string[];

  /**
   * Button that triggers the add event
   */
  @State() buttonElement?: HTMLButtonElement;

  /**
   * Emitted Event when the nodeguid was added to the store
   */
  @Event() added!: EventEmitter<void>;

  @Listen('pressbinder.change', { target: 'window' })
  onChange(_event: CustomEvent): void {
    this.store = this.getStoredEntries();
  }

  constructor() {
    this.store = this.getStoredEntries();
  }

  componentWillRender(): void {
    this.disabled = this.store.includes(this.nodeguid);
  }

  render() {
    const { animation, disabled, buttonElement, nodeguid, label } = this;

    return (
      <Host>
        <button class="flex items-center gap-2 py-1"
                ref={nativeElement => this.buttonElement = nativeElement}
                onClick={() => this.add(nodeguid)}
                disabled={this.disabled}>

          <div class="h-[35px] w-[35px] overflow-visible">
            { this.renderAnimatedFolder(disabled, animation, buttonElement) }
          </div>

          {label && (
            <span class={{
              "inline-block text-[18px] font-normal leading-[30px] hover:underline": true,
              "text-grey": !this.disabled,
              "text-grey-lighter": this.disabled
            }}>
              { label }
            </span>
          )}

        </button>
      </Host>
    );
  }

  private renderAnimatedFolder(disabled: boolean, animation: string, interactionElement: HTMLElement): string {
    if (disabled) {
      return (
        <span class="text-grey-lighter relative top-[5px] left-[5px]">
          <svg xmlns="http://www.w3.org/2000/svg"
              data-name="2px_Line_Icons"
              viewBox="43.99 45 40.01 38.01"
              width="26"
              height="26">
                <path d="M49,52V46H60l3,6H83V79a3.16,3.16,0,0,1-5.23,2.23A2.87,2.87,0,0,1,77,79V57H45V79a2.83,2.83,0,0,0,.77,2.23A2.91,2.91,0,0,0,48,82c.84,0,24,0,24,0"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"></path>
          </svg>
        </span>
      );
    }
    else {
      return (
        <vds-animated-icon
            src={animation}
            width="65px"
            height="65px"
            interactionElement={interactionElement}
            initialSegment={0}
            playSegmentStart={50}
            playSegmentEnd={100}
            clickSegmentStart={215}
            clickSegmentEnd={300}
            transform="translate3d(-12px, -14px, 0px)"
            hover={true}>
        </vds-animated-icon>
      );
    }
  }

  private getStoredEntries(): string[] {
    const data = sessionStorage.getItem('pressBinder');
    return JSON.parse(data) ?? [];
  }

  private add(nodeguid: string): void {
    setTimeout(() => {
      const store = [...this.store, nodeguid];
      sessionStorage.setItem('pressBinder', JSON.stringify(store));
      this.store = store;
      this.notifyPressBinder(nodeguid);
    }, 1000)
  }

  private notifyPressBinder(nodeguid: string): void {
    const ngPressBinder = document.querySelector('vdx-search-press-binder');
    if (ngPressBinder) {
      const event = new CustomEvent('add', { detail: nodeguid })
      ngPressBinder.dispatchEvent(event);
    }
  }

}
